/**
 * BookingComingSoon — soft-launch CTA shown on the Lake Detail page in place
 * of the live booking flow. Captures waitlist interest per-lake while we
 * onboard fishery partners.
 */
import { useState } from 'react';
import { Calendar, Bell, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingComingSoon = ({ lakeId, lakeName, testId = 'book-session-btn' }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('carpcast_token');
      await axios.post(
        `${API}/waitlist`,
        {
          email: email.trim().toLowerCase(),
          interest: 'fishery_bookings',
          name: lakeName,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setJoined(true);
      toast.success("You're on the list for booking updates.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="btn-gold"
          data-testid={testId}
          onClick={() => setOpen(true)}
        >
          <Calendar className="w-4 h-4 mr-2" />
          Bookings Coming Soon
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-[#0b0d0b] border-white/10 max-w-md"
        data-testid="booking-coming-soon-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
            Direct booking — almost here
          </DialogTitle>
          <DialogDescription className="text-[#a8b0a8] leading-relaxed">
            We&apos;re working with fisheries across the UK to bring day tickets
            and overnight sessions directly into Carp Cast. Leave your email and
            we&apos;ll notify you the moment {lakeName ? <span className="text-white font-semibold">{lakeName}</span> : 'this fishery'} goes live for booking.
          </DialogDescription>
        </DialogHeader>

        {joined ? (
          <div className="mt-4 rounded-xl border border-[#c8b273]/40 bg-[#c8b273]/5 px-4 py-4 flex items-start gap-3" data-testid="booking-waitlist-joined">
            <Check className="h-4 w-4 mt-0.5 text-[#c8b273] flex-shrink-0" />
            <div className="text-sm">
              <p className="font-bold uppercase tracking-widest text-[#c8b273] text-xs">You&apos;re on the list</p>
              <p className="text-[#a8b0a8] mt-1 leading-relaxed">
                We&apos;ll email you the moment booking opens here.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-4" data-testid="booking-waitlist-form">
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
              data-testid="booking-waitlist-email"
              required
            />
            <Button
              type="submit"
              disabled={submitting}
              className="btn-gold w-full"
              data-testid="booking-waitlist-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Notify me when bookings open
            </Button>
            <p className="text-[10px] text-[#6b7d6b] text-center">
              No spam. One email when bookings go live at this fishery.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingComingSoon;
